<template>
  <div id="tripla-bwapp" class="tripla-bwapp">
    <div
      class="tripla-bwapp-widget"
      :class="{
        active: isActive && !isMobile,
        'w-660px': !showDropdown,
        'w-860px': showDropdown,
      }"
    >
      <div v-if="showDropdown" class="tripla-bwapp-widget__hotels-dropdown">
        <HotelSelect
          :hotels="hotels"
          :is-active="isActive"
          :is-error="isHotelSelectedInvalid"
          @update:selectedHotel="onSelectHotel"
          @onOpen="onOpenHotelSelect"
        />
      </div>
      <div class="tripla-bwapp-widget__calendar">
        <BookingCalendar
          :is-active="isActive"
          :lang="lang"
          :hotel-code="hotelCode"
          :is-show-inventory="isShowInventory"
          :is-show-hotel-select="showDropdown"
        />
      </div>

      <div class="tripla-bwapp-widget__room-select">
        <RoomSelect
          :is-tiers-support="isKidTiersSupport"
          :is-active="isActive"
          :rules="rules"
        />
      </div>

      <div class="tripla-bwapp-widget__promo-code">
        <PromoCode :is-active="isActive" />
      </div>

      <div class="tripla-bwapp-widget__search-button">
        <div class="tripla-bwapp-widget__search-button__button">
          <a
            ref="searchButtonRef"
            :href="href"
            target="_blank"
            class="m-btn m-btn__yellow"
            @click="onSearchHotel"
          >
            {{ $t("booking.search") }}
          </a>
        </div>
      </div>
    </div>

    <div
      class="tripla-bwapp__overlay"
      v-if="isActive && !isMobile"
      @click="closeAll"
    ></div>
  </div>
</template>

<script>
import RoomSelect from "@/components/RoomSelect";
import PromoCode from "@/components/PromoCode";
import BookingCalendar from "@/components/BookingCalendar";
import { mapActions, mapGetters, mapState } from "vuex";
import moment from "moment";
import Calendar from "./components/calendarHelper";
import toggleClass from "@/constants/toggleClass";
import HotelSelect from "./components/HotelSelect.vue";
import { BRAND_CONFIG } from "./service/brandConfig.js";

const DEFAULT_BOOKING_DOMAIN = "https://booking.iconia.co.jp";
const DEFAULT_BRAND_ID = 297;

export default {
  name: "MyStaysBooking",
  components: {
    RoomSelect,
    PromoCode,
    BookingCalendar,
    HotelSelect,
  },
  data() {
    return {
      isActive: false,
      hotelCode: "",
      type: "",
      lang: "",
      isMobile: false,
      hotelInfo: {},
      isBookingShow: false,
      isShowInventory: false,
      roomMessage: [],
      isCallInventory: true,
      rules: {
        rulesTitle: "",
        rulesContent: "",
      },
      showDropdown: false,
      hotels: [],
      hotelSelect: null,
      isSubmitted: false,
      brandId: DEFAULT_BRAND_ID,
      bookingDomain: DEFAULT_BOOKING_DOMAIN,
    };
  },
  mounted() {
    window.dataLayer = window.dataLayer || [];

    import(
      /* webpackChunkName: "widgetsetting" */ "../src/service/widgetSetting.js"
    ).then(({ default: widgetConfig }) => {
      this.hotelInfo = widgetConfig;
      this.type = widgetConfig.type;
      const brandConfig = widgetConfig?.brand;

      let apiKey = process.env.VUE_APP_API_KEY || "";
      let apiSecret = process.env.VUE_APP_API_SECRET || "";

      if (brandConfig?.id && brandConfig.hotels?.length) {
        this.showDropdown = !!brandConfig.hotels?.length;
        this.hotels = brandConfig?.hotels || [];
        this.hotelCode = brandConfig.hotels?.length
          ? ""
          : widgetConfig.hotelCode;
        this.brandId = brandConfig.id || DEFAULT_BRAND_ID;
        const currentBrandConfig = BRAND_CONFIG[this.brandId] || {};
        apiKey = brandConfig.apiKey || currentBrandConfig.apiKey || apiKey;
        apiSecret = brandConfig.apiSecret || currentBrandConfig.apiSecret || apiSecret;
        this.bookingDomain = brandConfig.bookingUrl || currentBrandConfig.bookingUrl || DEFAULT_BOOKING_DOMAIN;
      } else {
        this.hotelCode = widgetConfig.hotelCode;
      }

      this.isShowInventory = widgetConfig.hotels?.length
        ? false
        : widgetConfig.showInventory;

      this.rules = {
        rulesTitle: widgetConfig.rulesTitle,
        rulesContent: widgetConfig.rulesContent,
      };

      this.applyCouponFromUrl();
      this.getClientSession({
        key: apiKey || process.env.VUE_APP_API_KEY,
        secret: apiSecret || process.env.VUE_APP_API_SECRET,
        brandId: this.brandId,
      });
      this.saveTiers({
        tiers: widgetConfig.tiers,
        lang: widgetConfig.lang,
        disableBedOption: widgetConfig.disableBedOption,
      });
      this.lang = widgetConfig.lang;
      this.$i18n.locale = widgetConfig.lang;
    });
    this.saveDeviceInfo();

    const { checkIn, checkOut } = this.bookingData;
    const dateFormat = "YYYY-MM-DD";
    const today = moment().format(dateFormat);
    if (
      moment(new Date(today)).isAfter(checkIn) ||
      moment(new Date(today)).isAfter(checkOut) ||
      moment(new Date(checkIn)).isAfter(checkOut)
    ) {
      const newCheckIn = moment()
        .day(7)
        .format(dateFormat);
      const newCheckOut = moment()
        .day(8)
        .format(dateFormat);
      this.setBooking({ checkIn: newCheckIn, checkOut: newCheckOut });
    }

    this.getInitialBooking();
    this.roomMessage[this.roomActive] = this.roomLeft === "NaN";
  },
  computed: {
    ...mapState({
      bookingState: (state) => state.bookingState,
      bookingData: (state) => state.bookingData,
      client_session: (state) => state.client_session,
      roomActive: (state) => state.currentRoomActive,
    }),
    ...mapGetters({
      isKidTiersSupport: "isKidTiersSupport",
      numberAdults: "numberAdults",
      numberChildren: "numberChildren",
      totalNights: "totalNights",
      roomList: "roomList",
      currentRoomActive: "currentRoomActive",
      roomLeft: "roomLeft",
      isRoomValid: "isRoomValid",
      kidTiers: "kidTiers",
      isBedOptionDisabled: "isBedOptionDisabled",
    }),

    bookingUrl(){
      return `${this.bookingDomain}/#/booking/result`;
    },

    // Same `roomList`, but with every child's `bed` forced to false when the
    // bed toggle is disabled - so a stale `true` from before it was disabled
    // (e.g. persisted in localStorage) never reaches the kids-tier matching
    // used to build `kids_age_range` for the inventory/calendar API.
    roomListForTierMatch() {
      if (!this.isBedOptionDisabled) {
        return this.roomList;
      }

      return this.roomList.map((room) => ({
        ...room,
        children: {
          ...room.children,
          childrenList: room.children.childrenList.map((child) => ({
            ...child,
            bed: false,
          })),
        },
      }));
    },

    href() {
      if (!this.isRoomValid || (this.showDropdown && !this.hotelSelect)) {
        return undefined;
      }

      const searchHotelParams = {
        code: this.hotelCode,
        checkin: this.bookingData.checkIn,
        checkout: this.bookingData.checkOut,
        room_type_codes: "",
        type: this.type,
        coupon_code: this.bookingData.promoCode,
        adults: this.currentRoomActive.adults.number,
        children: this.currentRoomActive.children.childrenList.length,
        room_count: this.roomList.length,
        locale: this.lang,
      };

      const firstRoom = this.roomList[0];
      const numberAdults = firstRoom.adults.number;
      const numberChildren = firstRoom.children.childrenList.length;
      const kids_age = firstRoom.children.childrenList.map((child) => {
        return {
          age: child.age,
          // Bed sharing can't be selected while the toggle is hidden, so
          // never report a stale `true` left over from before it was disabled.
          bed: this.isBedOptionDisabled ? false : child.bed,
          count: 1,
        };
      });

      const searchHotelWithOneRoom = {
        code: this.hotelCode,
        checkin: this.bookingData.checkIn,
        checkout: this.bookingData.checkOut,
        room_type_codes: "",
        type: this.type,
        coupon_code: this.bookingData.promoCode,
        adults: numberAdults,
        children: numberChildren,
        kids_ages: JSON.stringify(kids_age),
        room_count: this.roomList.length,
        locale: this.lang,
      };

      const triplaLinkWithOneRoom = `${this.bookingUrl}?${this.URLSearchParams(
        searchHotelWithOneRoom
      )}`;
      const triplaLinkWithMultipleRoom = `${
        this.bookingUrl
      }?${this.URLSearchParams(searchHotelParams)}`;

      if (this.isKidTiersSupport) {
        return triplaLinkWithOneRoom;
      }

      return triplaLinkWithMultipleRoom;
    },

    showInventory() {
      return this.isShowInventory && this.hotelSelect;
    },

    isHotelSelectedInvalid() {
      return this.isSubmitted && !this.hotelSelect;
    },
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  watch: {
    bookingState(state) {
      this.isBookingShow = true;
      if (
        state.checking ||
        state.promoCode ||
        state.roomSelect ||
        state.hotelDropdown
      ) {
        this.isActive = true;
      } else {
        this.isActive = false;
      }

      if (state.promoCode && this.isMobile) {
        this.scrollToTop();
        this.isActive = false;
      }

      if (this.isCallInventory && state.roomSelect && this.hotelSelect) {
        if (this.client_session) {
          this.getInventory();
        }
        this.isCallInventory = false;
      }
    },

    isActive(isActive) {
      if (isActive) {
        toggleClass("addClass", "no-scroll");
        this.scrollToTop();
      } else {
        toggleClass("removeClass", "no-scroll");
      }
    },

    isMobile(isMobile) {
      if (isMobile) {
        this.getDeviceInfo(true);
      } else {
        this.getDeviceInfo(false);
      }
    },

    client_session() {
      if (!this.showDropdown) {
        if (this.hotelInfo.tiers) {
          this.getTiers(this.hotelCode);
        }
      } else {
        if (this.hotelSelect) {
          this.getTiers(this.hotelCode);
        }
      }
    },

    roomList: {
      handler() {
        if (this.isBookingShow && this.isShowInventory && this.hotelSelect) {
          if (this.client_session) {
            this.getInventory();
          }
        }
        this.roomMessage[this.roomActive] = this.roomLeft === "NaN";
      },
      deep: true,
    },

    roomLeft(roomLeft) {
      this.roomMessage[this.roomActive] = roomLeft === "NaN";
    },
  },
  destroyed() {
    this.closeAll();
  },
  methods: {
    ...mapActions({
      closeAll: "closeAll",
      getDeviceInfo: "getDeviceInfo",
      saveTiers: "saveTiers",
      getTiers: "getTiers",
      getClientSession: "getClientSession",
      setBooking: "setInitialBooking",
      getInitialBooking: "getInitialBooking",
      showRoomSelect: "showRoomSelect",
      handleGetInventorySuccess: "handleGetInventorySuccess",
      savePromoCode: "savePromoCode",
      showChecking: "showChecking",
      showHotelDropdown: "showHotelDropdown",
    }),

    applyCouponFromUrl() {
      const promo = this.$route.query.coupon_code;
      if (promo && promo.length > 10) {
        const newPromo = promo.substring(0, 10);
        this.savePromoCode(newPromo);
      } else {
        this.savePromoCode(promo);
      }
    },

    scrollToTop() {
      const triplaBwapp = document.getElementById("tripla-bwapp");
      const paddingTop = this.isMobile ? 75 : 90;
      window.scrollTo({
        top: this.getOffsetTop(triplaBwapp) - paddingTop,
        left: 0,
        behavior: "smooth",
      });
    },

    getOffsetTop(element) {
      const rect = element.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      return rect.top + scrollTop;
    },

    saveDeviceInfo() {
      this.isMobile = window.innerWidth < 768;
      window.addEventListener("resize", this.onResize);
    },

    getInventory() {
      const { checkIn } = this.bookingData;
      const date = new Date(checkIn);
      const y = date.getFullYear();
      const m = date.getMonth();
      Calendar.getAPICalendar(this.inventoryParams(y, m), this.kidTiers)
        .then((res) => {
          this.handleGetInventorySuccess(res);
        })
        .catch((err) => console.log(err));
    },

    onResize(event) {
      this.isMobile = event.target.innerWidth < 768;
    },

    URLSearchParams(obj) {
      return new URLSearchParams(obj);
    },

    inventoryParams(y, m) {
      return {
        q: {
          checkin_date: "",
          checkout_date: "",
          adults: "",
          children: "",
          nights: this.totalNights,
        },
        hotelID: this.hotelCode,
        year: y,
        month: m,
        roomList: this.roomListForTierMatch,
        roomIndex: this.roomActive,
      };
    },

    onSearchWithHotelSelect(e) {
      if (!this.hotelSelect) {
        e.preventDefault();
      }

      if (this.isRoomValid) {
        this.closeAll();
      } else {
        e.preventDefault();
        this.showRoomSelect();
      }
    },

    onSearchWithoutHotelSelect(e) {
      if (this.isRoomValid) {
        this.closeAll();
      } else {
        e.preventDefault();
        this.showRoomSelect();
      }
    },

    onSearchHotel(e) {
      this.isSubmitted = true;
      if (this.showDropdown) {
        this.onSearchWithHotelSelect(e);
      } else {
        this.onSearchWithoutHotelSelect(e);
      }
      dataLayer.push(this.gtmParams());
    },

    gtmParams() {
      return {
        event: "bookNowHome",
        checkInDate: this.bookingData.checkIn,
        checkOutDate: this.bookingData.checkOut,
        productCoupon: this.bookingData.promoCode,
        language: this.lang,
        pageCategory: "",
        searchTerm: this.hotelInfo.hotelId,
        numberOfAdult: this.numberAdults,
        numberOfChildren: this.numberChildren,
        numberOfRooms: this.roomList.length,
      };
    },

    onSelectHotel(hotel) {
      if (!hotel) return;
      this.hotelSelect = hotel;
      this.hotelCode = hotel.code;
      this.isShowInventory = true;
      this.getTiers(this.hotelCode);
      this.showChecking();
    },

    onOpenHotelSelect() {
      this.showHotelDropdown();
    },
  },
};
</script>

<style lang="scss">
@import "assets/style.scss";

@font-face {
  font-family: "Lato";
  src: url("assets/fonts/Lato/Lato-Regular.eot");
  src: url("assets/fonts/Lato/Lato-Regular.woff") format("woff"),
    url("assets/fonts/Lato/Lato-Regular.ttf") format("truetype");
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: "icomoon";
  src: url("assets/icomoon/fonts/icomoon.eot?");
  src: url("assets/icomoon/fonts/icomoon.eot?#iefix")
      format("embedded-opentype"),
    url("assets/icomoon/fonts/icomoon.ttf?") format("truetype"),
    url("assets/icomoon/fonts/icomoon.woff?") format("woff");
  font-weight: normal;
  font-style: normal;
  font-display: block;
}

#tripla-bwapp {
  font-family: "Lato", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 15px;
}

.tripla-bwapp-widget {
  display: flex;
  flex-direction: column;
  padding-bottom: 25px;
  width: 100%;
  height: 100%;
  left: 0;
  right: 0;
  z-index: 100;

  @include min-sm {
    position: static;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    margin: 0 auto;
    padding: 15px 25px 25px 25px;
  }

  .tripla-bwapp-widget__hotels-dropdown {
    width: 100%;
    margin-bottom: 20px;

    @include min-sm {
      margin-bottom: 0;
      max-width: 300px;
    }
  }

  &.w-660px {
    @include min-sm {
      max-width: 660px;
    }
  }

  &.w-860px {
    @include min-sm {
      max-width: 920px;
    }
  }

  &__calendar {
    width: 100%;

    @include min-sm {
      width: 200px;
    }
  }

  &__promo-code {
    width: 100%;
    margin-bottom: 20px;

    @include min-sm {
      margin-bottom: 0;
      max-width: 96px;
    }
  }

  &__search-button {
    &__title {
      font-size: 13px;
      margin-bottom: 12px;
      display: none;

      @include min-sm {
        display: block;
      }
    }

    &__button {
      a {
        width: 100%;
        padding: 13px 20px 14px 20px;
        cursor: pointer;
        display: block;

        @include min-sm {
          padding: 17px 20px 18px 20px;
          min-width: 90px;
          width: initial;
        }
      }
    }
  }
}

.active {
  z-index: 1001;
  background: #303f62;
  position: relative;
}

.tripla-bwapp {
  &__overlay {
    position: fixed;
    left: 0;
    top: 0;
    opacity: 0.85;
    width: 100%;
    z-index: 1000;
    height: 100%;
    width: 100%;
    background: #232e48;
  }
}
</style>
